import { useCallback, useEffect } from 'react'

import { useFormContext, useWatch } from 'react-hook-form'
import { formatUnits, parseUnits } from 'viem'

import { useClearSwapData } from '~/components/forms/swap/hooks/useSwapHelpers'
import { getPaymentSourceQuoteError } from '~/components/forms/transfer/hooks/getPaymentSourceQuoteError'
import {
  useIsInsufficientFunds,
  useSwapPreview,
} from '~/components/forms/transfer/hooks/useTransferForm'
import {
  getValidatedRecipients,
  TransferFormValues,
} from '~/components/forms/transfer/transferSchema'
import { useTokenMetadata } from '~/hooks/useTokenMetadata'
import { EarnAction } from '~/types'
import { EvmAddress } from '~/utils/address'
import { getTransferCalls } from '~/utils/smartAccounts/transactions'
import { getSettlement } from '~/utils/swap'
import { getVaultAsset, isVaultToken } from '~/utils/vault'

export const useTransferQuote = (options?: { isPrivate?: boolean }) => {
  const { isPrivate } = options ?? {}
  const methods = useFormContext<TransferFormValues>()

  const { control, setValue, setError, clearErrors, getValues } = methods

  const sender = useWatch({ control, name: 'sender' })
  const transferToken = useWatch({ control, name: 'transferToken' })
  const swapData = useWatch({ control, name: 'swapData' })
  const recipients = useWatch({ control, name: 'recipients' })

  const transferTokenMetadata = useTokenMetadata({
    address: transferToken?.token,
    chainId: transferToken?.chainId,
  })

  const sellTokenMetadata = useTokenMetadata({
    address: swapData?.sellToken?.token,
    chainId: swapData?.sellToken?.chainId,
  })

  const isInsufficientFunds = useIsInsufficientFunds({
    sender,
    token: transferToken?.token,
    chainId: transferToken?.chainId,
    amount: transferToken?.amount,
  })

  const {
    swapQuote,
    isValidKey,
    isLoading: isLoadingSwapQuote,
    dataUpdatedAt,
    refetch,
    failureCount,
    failureReason,
    isDirectionReversed,
    reverseSwapQuote,
  } = useSwapPreview({
    formData: getValues(),
    isPrivate,
  })

  const reverseQuote = useCallback(() => {
    setValue('isDirectionReversed', !getValues('isDirectionReversed'))
  }, [setValue, getValues])

  const clearSwapData = useClearSwapData()

  // block the form from being submitted if the quote key is invalid
  useEffect(
    function blockFormSubmission() {
      if (!isValidKey && isInsufficientFunds) {
        setError('swapData', {
          type: 'manual',
        })
      } else if (!isLoadingSwapQuote) {
        clearErrors('swapData')
      }
    },
    [
      isValidKey,
      setError,
      clearErrors,
      isInsufficientFunds,
      isLoadingSwapQuote,
    ],
  )

  // A swap happens when a sell token is selected (auto-selected when short, or
  // chosen in the dropdown), not when funds fall short — so an explicit choice
  // isn't wiped once the balance covers a direct send.
  const hasSwapSource = !!swapData?.sellToken?.token

  // This effect updates the form depending on the swap quote and balance.
  useEffect(
    function updateTransferFormFields() {
      if (failureCount > 0) {
        setValue('calls', [])
        // Keep the source selected so it can surface an inline error, but clear
        // the resolved-quote artifacts — the router (which gates Review) plus any
        // stale sell amount / provider id left over from a prior success, so the
        // row drops back to its estimate instead of showing a leftover spend.
        setValue('swapData.swapRouter', undefined)
        setValue('swapData.providerTransactionId', undefined)
        setValue('swapData.settlementRouter', undefined)
        if (hasSwapSource) {
          setValue('swapData.sellToken.amount', '0')
        }
        return
      }

      const isSwapping = hasSwapSource

      // No swap: direct transfer if the balance covers it, else nothing to submit.
      if (!isSwapping) {
        // Private transfers must always route through a provider — never fall
        // back to direct transfer calls. Stay unsubmittable until a sell
        // source is committed and a private quote resolves.
        if (isPrivate) {
          setValue('calls', [])
          return
        }
        if (!isInsufficientFunds) {
          clearSwapData()

          // If the user has selected a token and has a recipient and amount we set the calls to transfer the tokens.
          if (
            transferToken?.token &&
            recipients.length > 0 &&
            EvmAddress.validate(transferToken.token) &&
            recipients.every(
              (r) =>
                r.receiver.address && EvmAddress.validate(r.receiver.address),
            ) &&
            recipients.every(
              (recipient) =>
                recipient.receiver.address &&
                recipient.amount &&
                parseFloat(recipient.amount) > 0,
            )
          ) {
            setValue(
              'calls',
              getTransferCalls({
                token: EvmAddress.format(transferToken.token),
                recipients: getValidatedRecipients(recipients).map(
                  (recipient) => ({
                    address: EvmAddress.format(recipient.receiver.address),
                    amount: parseUnits(
                      recipient.amount,
                      transferTokenMetadata?.decimals ?? 18,
                    ),
                  }),
                ),
              }),
            )
          }
        } else {
          setValue('calls', [])
        }
        return
      }

      // A swap is happening → drive the transfer from the quote. Stay
      // unsubmittable until a quote for the CURRENT inputs resolves, clearing
      // the stale router during the debounce so Review can't enable on a
      // previous amount's quote.
      if (!swapQuote || !isValidKey) {
        setValue('calls', [])
        setValue('swapData.swapRouter', undefined)
        return
      }

      // If there is a swap quote and the quote key (swap quote matches the input) is valid we set the swap data and calls.
      if (swapQuote && isValidKey) {
        const settlement = getSettlement(swapQuote.data)
        setValue('swapData.swapRouter', swapQuote.data.routerType)
        setValue(
          'swapData.providerTransactionId',
          settlement?.providerTransactionId,
        )
        setValue('swapData.settlementRouter', settlement?.routerType)
        setValue('swapData.sellToken.chainId', swapQuote.data.chainId)
        setValue(
          'swapData.sellToken.amount',
          formatUnits(
            swapQuote.data.sellAmount,
            sellTokenMetadata?.decimals ?? 18,
          ),
        )

        if (isVaultToken(swapQuote.data.chainId, swapQuote.data.sellToken)) {
          setValue(
            'swapData.earnToken.amount',
            formatUnits(
              swapQuote.data.steps?.[0]?.buyAmount ?? swapQuote.data.buyAmount,
              sellTokenMetadata?.decimals ?? 18,
            ),
          )
        }

        if (isVaultToken(swapQuote.data.chainId, swapQuote.data.buyToken)) {
          setValue('swapData.earnToken', {
            token: getVaultAsset(
              swapQuote.data.chainId,
              swapQuote.data.buyToken,
            )!,
            chainId: swapQuote.data.chainId,
            amount: formatUnits(
              swapQuote.data.steps?.[0]?.buyAmount ?? swapQuote.data.sellAmount,
              transferTokenMetadata?.decimals ?? 18,
            ),
            action: EarnAction.DEPOSIT,
          })
        }

        const calls = swapQuote.data.calls.map((call) => ({
          to: call.to,
          data: call.data,
          value: BigInt(call.value),
        }))

        // If there are multiple recipients we add the transfer calls for each recipient.
        // Currently, if there is just one recipient, the swap quote contains the transfer call for the recipient
        // (because the server is adding the transfer call)
        if (recipients.length > 1) {
          calls.push(
            ...getTransferCalls({
              token: EvmAddress.format(transferToken.token),
              recipients: getValidatedRecipients(recipients).map(
                (recipient) => ({
                  address: EvmAddress.format(recipient.receiver.address),
                  amount: parseUnits(
                    recipient.amount,
                    transferTokenMetadata?.decimals ?? 18,
                  ),
                }),
              ),
            }),
          )
        }

        setValue('calls', calls, { shouldValidate: true })
      }
    },
    [
      swapQuote,
      setValue,
      sellTokenMetadata?.decimals,
      isInsufficientFunds,
      hasSwapSource,
      transferTokenMetadata?.decimals,
      recipients,
      transferToken,
      isValidKey,
      failureCount,
      clearSwapData,
      isPrivate,
    ],
  )

  return {
    reverseQuote,
    isDirectionReversed,
    reverseSwapQuote,
    isLoading: isLoadingSwapQuote,
    dataUpdatedAt,
    refetchQuote: refetch,
    quoteError: getPaymentSourceQuoteError({
      quoteFailed: hasSwapSource && failureCount > 0,
      failureReason,
      sellToken: sellTokenMetadata,
    }),
  }
}
